/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited
 * SPDX-License-Identifier: Apache-2.0
 */
/** Transport-owned session epochs keep late responses out of a newer session. */
let snapshot: { generation: number; authenticated: boolean | undefined } = {
  generation: 0,
  authenticated: undefined,
};
let unauthorizedGeneration: number | undefined;
const listeners = new Set<() => void>();

export const getAuthSnapshot = () => snapshot;

/** Retained until an auth transition, including when no event listener is mounted. */
export const getPendingUnauthorizedGeneration = () => unauthorizedGeneration;

export const subscribeAuthGeneration = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Call after successful authentication (even for the same user) or logout. */
export const advanceAuthGeneration = (authenticated: boolean) => {
  // Repeated resets must not open a new epoch for each concurrent 401.
  if (!authenticated && snapshot.authenticated === false) {
    return;
  }
  unauthorizedGeneration = undefined;
  snapshot = { generation: snapshot.generation + 1, authenticated };
  listeners.forEach(listener => listener());
};

export const claimUnauthorized = (generation: number | undefined) => {
  if (
    generation !== snapshot.generation ||
    snapshot.authenticated === false ||
    unauthorizedGeneration === generation
  ) {
    return false;
  }
  unauthorizedGeneration = generation;
  return true;
};
